import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfile } from '../../../types/types';
import { RestUser } from '../../../services/auth/types/user';
import { getTrips } from '../../../services/trips/api/tripsApi';
import DashboardLayout from '../../layouts/DashboardLayout';
import { ProfileTripPreview, getVisibleTripTags, mapTripToProfilePreview } from '../../utils/mapTripToProfilePreview';
import './ProfilePage.css';

interface ProfilePageProps {
  user: UserProfile;
  restUser: RestUser | null;
}

const fallbackAvatar = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
  <defs>
    <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
      <stop stop-color="#0d4f3c"/>
      <stop offset="1" stop-color="#d9ff31"/>
    </linearGradient>
  </defs>
  <rect width="128" height="128" rx="64" fill="url(#g)"/>
  <circle cx="64" cy="48" r="23" fill="#f7efe6"/>
  <path d="M24 116c7-25 22-38 40-38s33 13 40 38" fill="#f7efe6"/>
</svg>
`)}`;

const profilePosts = [
  {
    id: 'santorini-gallery',
    location: 'Santorini, Greece · 6h',
    badge: 'Save trip',
    caption: 'Blue domes, white walls, and slow mornings above the caldera. Saving this route for anyone planning a first visit.',
    images: [
      'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?auto=format&fit=crop&w=900&q=80',
      'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?auto=format&fit=crop&w=700&q=80',
      'https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?auto=format&fit=crop&w=700&q=80',
    ],
  },
  {
    id: 'mediterranean-note',
    location: 'Mediterranean route · 1d',
    badge: 'Saved',
    caption: 'A slow month through the islands feels like the right pace: fewer stops, better meals, longer sunsets.',
    images: [
      'https://images.unsplash.com/photo-1516483638261-f4dbaf036963?auto=format&fit=crop&w=900&q=80',
    ],
  },
];

const ProfilePage: React.FC<ProfilePageProps> = ({ user, restUser }) => {
  const navigate = useNavigate();
  const tripsScrollerRef = React.useRef<HTMLDivElement>(null);
  const [profileTrips, setProfileTrips] = React.useState<ProfileTripPreview[]>([]);
  const [isLoadingTrips, setIsLoadingTrips] = React.useState(true);
  const [tripsError, setTripsError] = React.useState<string | null>(null);
  const profileName = user?.name && !user.name.includes('@') ? user.name : '';
  const displayName = [restUser?.firstName, restUser?.lastName].filter(Boolean).join(' ') || profileName || 'Emma Wilson';
  const avatar = restUser?.picture_url || restUser?.avatarUrl || user?.avatar || fallbackAvatar;
  const joinedYear = restUser?.createdAt ? new Date(restUser.createdAt).getFullYear() : '2023';
  const profileHandle = restUser?.nickname || restUser?.email || user?.email || 'traveler';
  const isCurrentUser = true;
  const hasMoreTrips = profileTrips.length > 3;

  const fetchProfileTrips = React.useCallback(async () => {
    try {
      setIsLoadingTrips(true);
      setTripsError(null);
      const fetchedTrips = await getTrips();
      setProfileTrips(fetchedTrips.map((trip, index) => mapTripToProfilePreview(trip, index)));
    } catch (err: any) {
      setTripsError(err.message || 'Failed to load profile trips.');
    } finally {
      setIsLoadingTrips(false);
    }
  }, []);

  React.useEffect(() => {
    fetchProfileTrips();
  }, [fetchProfileTrips]);

  const scrollTrips = (direction: 'previous' | 'next') => {
    tripsScrollerRef.current?.scrollBy({
      left: direction === 'next' ? 216 : -216,
      behavior: 'smooth',
    });
  };

  const openTrip = (selectedTrip: ProfileTripPreview) => {
    navigate(`/trips/${selectedTrip.id}`, {
      state: { backTo: '/profile', owned: isCurrentUser, trip: selectedTrip.sourceTrip },
    });
  };

  return (
    <DashboardLayout activeSection="settings" wide>
      <div className="v2-profile-page">
        <section className="v2-profile-hero" aria-label={`${displayName} profile`}>
          <div className="v2-profile-hero__cover" />
          <div className="v2-profile-hero__content">
            <img
              className="v2-profile-hero__avatar"
              src={avatar}
              alt=""
              onError={(event) => {
                event.currentTarget.src = fallbackAvatar;
              }}
            />
            <div className="v2-profile-hero__details">
              <div className="v2-profile-hero__title-row">
                <div>
                  <h1>{displayName}</h1>
                  <p>{profileHandle} · Joined {joinedYear}</p>
                </div>
                {!isCurrentUser && (
                  <div className="v2-profile-hero__actions">
                    <button type="button" className="v2-profile-hero__follow">+ Follow</button>
                    <button type="button" className="v2-profile-hero__message">Message</button>
                  </div>
                )}
              </div>
              <p className="v2-profile-hero__bio">
                Chasing sunsets, great food, and memorable routes. Planning and sharing trips with EnRoute.
              </p>
            </div>
          </div>
        </section>

        <section className="v2-profile-stats" aria-label="Profile stats">
          <ProfileStat value="42" label="Posts" />
          <ProfileStat value="2,140" label="Followers" />
          <ProfileStat value="312" label="Following" />
          <ProfileStat value={isLoadingTrips ? '...' : String(profileTrips.length)} label="Trips" />
        </section>

        <section className="v2-profile-section" aria-labelledby="profile-trips-heading">
          <div className="v2-profile-section__header">
            <h2 id="profile-trips-heading">Trips <span>· {profileTrips.length}</span></h2>
            <div className="v2-profile-section__actions">
              {hasMoreTrips && (
                <button
                  type="button"
                  className="v2-profile-section__see-all"
                  onClick={() => navigate('/profile/trips')}
                >
                  See all trips
                </button>
              )}
              <button type="button" aria-label="Previous trips" onClick={() => scrollTrips('previous')}>‹</button>
              <button type="button" aria-label="Next trips" onClick={() => scrollTrips('next')}>›</button>
            </div>
          </div>
          {isLoadingTrips && <ProfileTripsState message="Loading trips..." />}
          {!isLoadingTrips && tripsError && (
            <ProfileTripsState message={tripsError} actionLabel="Retry" onAction={fetchProfileTrips} />
          )}
          {!isLoadingTrips && !tripsError && profileTrips.length === 0 && (
            <ProfileTripsState message="No trips yet." />
          )}
          {!isLoadingTrips && !tripsError && profileTrips.length > 0 && (
            <div className="v2-profile-trips" ref={tripsScrollerRef}>
              {profileTrips.map((trip) => {
                const { visible: visibleTags, hasMore } = getVisibleTripTags(trip.tags);

                return (
                <button
                  type="button"
                  key={trip.id}
                  className="v2-profile-trip-card"
                  onClick={() => openTrip(trip)}
                >
                  <img src={trip.imageUrl} alt="" />
                  <div className="v2-profile-trip-card__content">
                    <h3>{trip.title}</h3>
                    {visibleTags.length > 0 && (
                      <div className="v2-profile-trip-card__tags" aria-label={`${trip.title} tags`}>
                        {visibleTags.map((tag) => (
                          <span key={tag}>{tag}</span>
                        ))}
                        {hasMore && <span className="v2-profile-trip-card__tags-more">more</span>}
                      </div>
                    )}
                    <p>{trip.meta}</p>
                  </div>
                </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="v2-profile-section v2-profile-section--posts" aria-labelledby="profile-posts-heading">
          <h2 id="profile-posts-heading">Posts</h2>
          <div className="v2-profile-posts">
            {profilePosts.map((post) => (
              <article key={post.id} className="v2-profile-post">
                <header>
                  <div className="v2-profile-post__author">
                    <img src={avatar} alt="" />
                    <div>
                      <strong>{displayName}</strong>
                      <span>{post.location}</span>
                    </div>
                  </div>
                  <button type="button">{post.badge}</button>
                </header>
                <div className={`v2-profile-post__media ${post.images.length > 1 ? 'v2-profile-post__media--gallery' : ''}`}>
                  {post.images.map((imageUrl) => (
                    <img key={imageUrl} src={imageUrl} alt="" />
                  ))}
                </div>
                <p>{post.caption}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

interface ProfileStatProps {
  value: string;
  label: string;
}

const ProfileStat: React.FC<ProfileStatProps> = ({ value, label }) => (
  <div className="v2-profile-stats__item">
    <strong>{value}</strong>
    <span>{label}</span>
  </div>
);

interface ProfileTripsStateProps {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

const ProfileTripsState: React.FC<ProfileTripsStateProps> = ({ message, actionLabel, onAction }) => (
  <div className="v2-profile-trips-state">
    <p>{message}</p>
    {actionLabel && onAction && (
      <button type="button" onClick={onAction}>
        {actionLabel}
      </button>
    )}
  </div>
);

export default ProfilePage;
